import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { moveLocation, searchLocations, type LocationDetail } from './api';

interface Props {
  location: LocationDetail;
  onDone: () => void;
  onCancel: () => void;
}

/**
 * Reparent a location.
 *
 * Search rather than a tree picker: the destination is usually known by name,
 * and unfolding a tree to find it is more work than typing three letters. The
 * server rejects a move into the node's own subtree, so the picker does not
 * have to reason about which targets are legal.
 */
export function MoveDialog({ location, onDone, onCancel }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: matches = [] } = useQuery({
    queryKey: ['locations', 'search', query],
    queryFn: () => searchLocations(query),
    enabled: query.trim().length > 0,
  });

  const move = useMutation({
    mutationFn: (parentId: string | null) => moveLocation(location.id, parentId),
    onSuccess: onDone,
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <div className="move">
      <div className="move__head">
        <strong>{t('locations.moveTitle', { name: location.name })}</strong>
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>

      {error && (
        <p className="panel__error">
          {t(`locations.errors.${error}`, t('locations.errors.unknown'))}
        </p>
      )}

      <input
        type="search"
        value={query}
        placeholder={t('locations.moveSearch')}
        onChange={(event) => setQuery(event.target.value)}
      />

      <ul className="move__results">
        <li>
          <button
            type="button"
            onClick={() => move.mutate(null)}
            disabled={location.parent_id === null || move.isPending}
          >
            {t('locations.moveToRoot')}
          </button>
        </li>
        {matches
          // Moving into itself is the one case worth hiding: the server would
          // refuse it, but offering it at all just invites the error.
          .filter((candidate) => candidate.id !== location.id)
          .map((candidate) => (
            <li key={candidate.id}>
              <button
                type="button"
                onClick={() => move.mutate(candidate.id)}
                disabled={move.isPending}
              >
                <span className="move__name">
                  {candidate.name}
                  {/* Names repeat freely now, so two "Третья полка" are told
                      apart only by where they sit. */}
                  {candidate.ancestors.length > 0 && (
                    <span className="move__where">({candidate.ancestors.join(' → ')})</span>
                  )}
                </span>
                <code>{candidate.code}</code>
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}
